import React, { useEffect, useMemo } from "react";
import { change as changeField, Field, formValueSelector } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import { components } from "react-select";
import { IconButton } from "@mui/material";

import { CustomCheckbox } from "../../../../../../../common/customCheckbox"
import { CustomSingleSelect } from "../../../../../../../common/new-core-ui/CustomSingleSelect";
import colorStyles from "../../../../FormSelectStyle";
import styles from "../../../../../../SwmsMasters/SwmsMastersStyle.module.css";
import jobStyles from "../../../../../JobStyle.module.css";
import layoutStyles from "../../../../../../SwmsMasters/EmergencyPlans/EmergencyPlansLayout.module.css";
import RescueEquipmentItemAddEditModal from "./RescueEquipmentItemAddEditModal";
import {
  countSelectedItems,
  getPartTypeId,
  isActive,
  normalizeItemId,
  normalizeSelectedItems,
  normalizeSelectedMethodItems,
  normalizeTypeId
} from "./rescueEquipmentStaging.utils";

const FORM_NAME = "JobSwmsV3EmergencyPlansContainerForm";
const SECTION_KEY = "rescue_equipment_staging";
const formSelector = formValueSelector(FORM_NAME);

const PARTS = [
  { key: "rescue_equipment", heading: "Rescue Equipment" },
  { key: "rescue_ppe", heading: "Rescue PPE" },
  { key: "licences_competencies", heading: "Licences & competencies" }
];

const RescueItemsMultiSelect = ({ input, meta, options, loading, methodItemTypesLoading, selectedTypeId, onSelectionChange }) => {
  const selectedIds = new Set(Object.keys(normalizeSelectedItems(input.value)).map(Number));
  const selectedOptions = options.filter((option) => selectedIds.has(Number(option.value)));

  return (
    <CustomSingleSelect
      input={{ ...input, value: selectedOptions }}
      meta={meta}
      placeholder="Select rescue items"
      options={options}
      styles={colorStyles}
      onChange={(nextOptions) => {
        const selectedMap = (Array.isArray(nextOptions) ? nextOptions : []).reduce((map, option) => {
          map[option.value] = true;
          return map;
        }, {});
        input.onChange(selectedMap);
        onSelectionChange(nextOptions);
      }}
      otherProps={{
        isMulti: true,
        closeMenuOnSelect: false,
        blurInputOnSelect: false,
        hideSelectedOptions: false,
        isLoading: methodItemTypesLoading,
        isDisabled: !selectedTypeId || loading || methodItemTypesLoading,
        components: { dropdownIndicator: <components.DownChevron /> }
      }}
    />
  );
};

export default function JobSwmsV3RescueEquipmentStagingSection({
  loading,
  sectionData,
  planIndex,
  methodItemTypeGroups = [],
  methodItemTypesLoading = false
}) {
  const dispatch = useDispatch();
  const [itemModal, setItemModal] = React.useState({ visible: false, mode: "Add", item: null, partKey: null });

  const safeSectionData = sectionData && sectionData.data ? sectionData.data : (sectionData || {});
  const formData = useSelector((state) => formSelector(
    state,
    `plans[${planIndex}].sections.${SECTION_KEY}.data`
  ));
  const liveSectionData = formData || safeSectionData;

  useEffect(() => {
    if (!formData) {
      return;
    }

    PARTS.forEach((part) => {
      const partData = formData[part.key] || {};
      const selectedItems = partData.selected_items;
      const normalizedItems = selectedItems !== undefined
        ? normalizeSelectedItems(selectedItems)
        : normalizeSelectedMethodItems(partData.method_items);
      if (JSON.stringify(selectedItems) !== JSON.stringify(normalizedItems)) {
        dispatch(changeField(
          FORM_NAME,
          `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.selected_items`,
          normalizedItems
        ));
      }
    });
  }, [dispatch, formData, planIndex]);

  const groupedById = useMemo(() => {
    const map = {};
    (Array.isArray(methodItemTypeGroups) ? methodItemTypeGroups : []).forEach((group) => {
      const id = normalizeTypeId(group && group.method_item_type_id);
      if (!id) {
        return;
      }

      map[id] = {
        id,
        name: group && group.method_item_type ? String(group.method_item_type) : `Type ${id}`,
        active: isActive(group && group.active),
        items: (Array.isArray(group && group.method_items) ? group.method_items : []).map((item) => ({
          id: normalizeItemId(item && item.id),
          name: item && item.method_item ? String(item.method_item) : "",
          active: isActive(item && item.active)
        })).filter((item) => item.id)
      };
    });
    return map;
  }, [methodItemTypeGroups]);

  const selectedTypeIdsByPart = useMemo(() => {
    const map = {};
    PARTS.forEach((part) => {
      map[part.key] = getPartTypeId(liveSectionData[part.key]);
    });
    return map;
  }, [liveSectionData]);

  const selectedItemIdsByPart = useMemo(() => {
    const map = {};
    PARTS.forEach((part) => {
      const partData = liveSectionData[part.key] && typeof liveSectionData[part.key] === "object"
        ? liveSectionData[part.key]
        : {};
      const selectedItems = partData.selected_items !== undefined
        ? normalizeSelectedItems(partData.selected_items)
        : normalizeSelectedMethodItems(partData.method_items);
      map[part.key] = new Set(Object.keys(selectedItems).map(Number));
    });
    return map;
  }, [liveSectionData]);

  const selectedTypeIds = useMemo(() => {
    return new Set(Object.values(selectedTypeIdsByPart).filter(Boolean));
  }, [selectedTypeIdsByPart]);

  const typeOptions = useMemo(() => {
    return Object.values(groupedById)
      .filter((group) => group.active || selectedTypeIds.has(group.id))
      .map((group) => ({
        value: group.id,
        label: group.name
      }));
  }, [groupedById, selectedTypeIds]);

  return (
    <div className="container-fluid pb-2">
      <div className="row pt-2">
        <div className="col-12">
          {PARTS.map((part) => {
            const partData = liveSectionData[part.key] && typeof liveSectionData[part.key] === "object"
              ? liveSectionData[part.key]
              : {};
            const selectedTypeId = selectedTypeIdsByPart[part.key];
            const selectedGroup = selectedTypeId ? groupedById[selectedTypeId] : null;
            const selectedItemIds = selectedItemIdsByPart[part.key] || new Set();
            const storedItems = Array.isArray(partData.method_items) ? partData.method_items : [];
            const items = storedItems.length > 0
              ? storedItems.map((item) => ({
                id: normalizeItemId(item.id !== undefined ? item.id : item.method_item_id),
                name: item.method_item || item.name || "",
                is_added: item.is_added === true,
                isNew: item.isNew === true || item.is_new === 1,
                is_new: item.is_new
              })).filter((item) => item.id)
              : (selectedGroup && Array.isArray(selectedGroup.items)
                ? selectedGroup.items.filter((item) => selectedItemIds.has(item.id))
                : []);
            const selectedItems = items;
            const itemOptions = Array.from(new Map([
              ...((selectedGroup && Array.isArray(selectedGroup.items)) ? selectedGroup.items : [])
                .map((item) => [String(item.id), { value: item.id, label: item.name }]),
              ...items.map((item) => [String(item.id), { value: item.id, label: item.name }])
            ]).values());
            const selectedItemCount = countSelectedItems(partData.selected_items);
            const otherSelectedTypeIds = PARTS
              .filter((candidate) => candidate.key !== part.key)
              .map((candidate) => selectedTypeIdsByPart[candidate.key])
              .filter(Boolean);
            const otherSelectedTypeSet = new Set(otherSelectedTypeIds);

            return (
              <div
                key={part.key}
                style={{
                  border: "1px solid #e4e0f0",
                  borderRadius: "8px",
                  background: "#fff",
                  marginBottom: "10px"
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "10px", background: "#F0EEF7", padding: "7px 11px" }}>
                  <div className={styles.Label} style={{ marginBottom: 0, fontSize: "11px", fontWeight: 800, letterSpacing: "0.03em", textTransform: "uppercase" }}>{part.heading}</div>
                  <span
                    style={{
                      minWidth: "68px",
                      height: "18px",
                      borderRadius: "4px",
                      fontSize: "10px",
                      fontWeight: 700,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "0",
                      lineHeight: 1,
                      flexShrink: 0,
                      background: "transparent",
                      color: "#6B5B95"
                    }}
                    title={`${selectedItemCount} selected`}
                    aria-label={`${selectedItemCount} selected`}
                  >
                    {selectedItemCount} selected
                  </span>
                </div>

                <div className="container-fluid mt-3">
                  <div className="row">
                    <div className="col-6">
                      <div className={jobStyles.Label}>Method Item Type</div>
                      <Field
                        name={`${part.key}.method_item_type`}
                        component={({ input, meta }) => (
                        <CustomSingleSelect
                          input={input}
                          meta={meta}
                          placeholder="Select method item type"
                          options={typeOptions.filter((option) => {
                            const optionTypeId = normalizeTypeId(option && option.value);
                            return optionTypeId === selectedTypeId || !otherSelectedTypeSet.has(optionTypeId);
                          })}
                          styles={colorStyles}
                          otherProps={{
                            hideSelectedOptions: false,
                            components: { dropdownIndicator: <components.DownChevron /> },
                            isLoading: methodItemTypesLoading,
                            isDisabled: false
                          }}
                          onChange={(nextOption) => {
                            input.onChange(nextOption || null);
                            dispatch(changeField(
                              FORM_NAME,
                              `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.method_item_type_id`,
                              nextOption && nextOption.value ? Number(nextOption.value) : null
                            ));
                            dispatch(changeField(
                              FORM_NAME,
                              `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.selected_items`,
                              {}
                            ));
                            dispatch(changeField(
                              FORM_NAME,
                              `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.method_items`,
                              []
                            ));
                          }}
                        />
                        )}
                      />
                    </div>
                    {!selectedTypeId ? (
                      <div className="col-6" style={{ color: "#6b7280", fontSize: "12px" }}>
                        Select a method item type to load items.
                      </div>
                    ) : null}
                    </div>
                  </div>

                <div>
                <div className="container-fluid my-3">
                    <div className="row">
                      <div className="col-12">
                        <div style={{ maxHeight: "300px", overflow: "hidden auto" }}>
                          {!selectedTypeId || selectedItems.length === 0 ? (
                            <div style={{ color: "#6b7280", fontSize: "12px", padding: "8px 0" }}>
                              No items selected
                            </div>
                          ) : null}
                          <div className="row" style={{ rowGap: "2px", columnGap: 0 }}>
                      {selectedItems.map((item) => {
                        const itemId = normalizeItemId(item && item.id);
                        const itemName = item && item.name ? item.name : `Item ${itemId}`;

                        return (
                          <div key={itemId} className="col-12 col-md-6 d-flex align-items-start py-1" style={{ flexWrap: "nowrap" }}>
                            <div className="d-flex align-items-start w-100" style={{ flex: "1 1 auto", minWidth: 0, gap: "8px" }}>
                            <div
                              role="button"
                              className={layoutStyles.MethodItemOption}
                              style={{ flex: 1, cursor: loading || methodItemTypesLoading ? "default" : "pointer" }}
                              tabIndex={loading || methodItemTypesLoading ? -1 : 0}
                              onClick={(event) => {
                                if (event.target && typeof event.target.closest === "function" && event.target.closest(".ant-checkbox-wrapper")) {
                                  return;
                                }
                                const field = event.currentTarget.querySelector("input[type=checkbox]");
                                if (field && !field.disabled) {
                                  field.click();
                                }
                              }}
                              onKeyDown={(event) => {
                                if (event.key !== "Enter" && event.key !== " ") {
                                  return;
                                }
                                event.preventDefault();
                                const field = event.currentTarget.querySelector("input[type=checkbox]");
                                if (field && !field.disabled) {
                                  field.click();
                                }
                              }}
                            >
                              <Field
                                name={`${part.key}.selected_items.${itemId}`}
                                component={CustomCheckbox}
                                disabled={loading || methodItemTypesLoading}
                                label={<span style={{ fontSize: "12.5px", lineHeight: "16px", color: "#212529" }}>{itemName}</span>}
                                className="compact-method-item-checkbox"
                              />
                            </div>
                              {item.is_added || item.isNew ? (
                                <div className="d-flex align-items-center" style={{ gap: "8px", paddingTop: "4px", flexShrink: 0 }}>
                                {item.isNew ? (
                                <IconButton
                                  type="button"
                                  size="small"
                                  disabled={loading || methodItemTypesLoading}
                                  aria-label={`Edit ${itemName}`}
                                  title={`Edit ${itemName}`}
                                  onClick={() => setItemModal({ visible: true, mode: "Edit", item: { ...item, method_item: item.name }, partKey: part.key })}
                                  style={{ height: "auto", padding: 0, marginTop: "3px", color: "#90bf00" }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="13" height="13" aria-hidden="true">
                                    <path fill="currentColor" d="M362.7 19.3L314.3 67.7L444.3 197.7L492.7 148.9C517.7 123.9 517.7 83.5 492.7 58.5L453.1 19.3C428.1-5.7 387.7-5.7 362.7 19.3zM291.7 90.3L58.6 323.5C48.2 333.9 40.4 347.8 36.3 362.4L1 481.2C-1.5 489.7 .8 498.8 7 505C13.2 511.2 22.3 513.5 30.8 511L150.6 475.6C165.2 471.5 179.1 463.7 189.5 453.3L421.7 220.3L291.7 90.3z" />
                                  </svg>
                                </IconButton>
                                ) : null}
                                <IconButton
                                  type="button"
                                  size="small"
                                  disabled={loading || methodItemTypesLoading}
                                  aria-label={`Remove ${itemName}`}
                                  title={`Remove ${itemName}`}
                                  onClick={() => {
                                    dispatch(changeField(
                                      FORM_NAME,
                                      `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.method_items`,
                                      items.filter((candidate) => candidate.id !== itemId)
                                    ));
                                    dispatch(changeField(
                                      FORM_NAME,
                                      `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.selected_items.${itemId}`,
                                      undefined
                                    ));
                                  }}
                                  style={{ height: "auto", padding: 0, marginTop: "3px", marginLeft: "6px", color: "#dc2626" }}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="14" height="14" aria-hidden="true">
                                    <path fill="currentColor" d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z" />
                                  </svg>
                                </IconButton>
                                </div>
                              ) : null}
                          </div>
                          </div>
                        );
                      })}
                          </div>
                        </div>
                      </div>
                      {itemModal.visible && itemModal.partKey === part.key ? (
                        <RescueEquipmentItemAddEditModal
                          visible={itemModal.visible}
                          mode={itemModal.mode}
                          initialValues={itemModal.item || {}}
                          onClose={(saved, item) => {
                            if (saved && item) {
                              const nextItem = itemModal.mode === "Edit"
                                ? item
                                : { ...item, id: Date.now(), isNew: true, is_new: 1 };
                              const nextItems = itemModal.mode === "Edit"
                                ? items.map((entry) => entry.id === nextItem.id ? { ...entry, ...nextItem } : entry)
                                : [...items, nextItem];
                              dispatch(changeField(
                                FORM_NAME,
                                `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.method_items`,
                                nextItems
                              ));
                              const currentSelectedItems = normalizeSelectedItems(partData.selected_items);
                              currentSelectedItems[nextItem.id] = true;
                              dispatch(changeField(
                                FORM_NAME,
                                `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.selected_items`,
                                currentSelectedItems
                              ));
                            }
                            setItemModal({ visible: false, mode: "Add", item: null, partKey: null });
                          }}
                        />
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="container-fluid mb-3">
                  <div className="row">
                    <div className="col-6">
                    <div className={jobStyles.Label}>Select Item</div>
                    <Field
                      name={`${part.key}.selected_items`}
                      component={RescueItemsMultiSelect}
                      options={itemOptions}
                      loading={loading}
                      methodItemTypesLoading={methodItemTypesLoading}
                      selectedTypeId={selectedTypeId}
                      onSelectionChange={(nextOptions) => {
                            const displayItems = Array.from(new Map([
                                ...items.map((item) => [String(item.id), {
                                  id: item.id,
                                  method_item: item.name,
                                  ...(item.is_added ? { is_added: true } : {}),
                                  ...(item.isNew ? { isNew: true } : {}),
                                  ...(item.is_new ? { is_new: item.is_new } : {})
                                }]),
                                ...(Array.isArray(nextOptions) ? nextOptions.map((option) => {
                                  const existing = items.find((item) => String(item.id) === String(option.value));
                                  return [String(option.value), existing || {
                                    id: option.value,
                                    method_item: option.label,
                                    is_added: true
                                  }];
                                }) : [])
                              ]).values());
                              dispatch(changeField(
                                FORM_NAME,
                                `plans[${planIndex}].sections.${SECTION_KEY}.data.${part.key}.method_items`,
                                displayItems
                              ));
                        }}
                      />
                    </div>
                    <div className="col-6 d-flex align-items-end">
                      <button
                        type="button"
                        disabled={loading || !selectedTypeId || methodItemTypesLoading}
                        className={jobStyles.PrimaryButton}
                        onClick={() => setItemModal({ visible: true, mode: "Add", item: null, partKey: part.key })}
                      >
                        Add Item
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
